import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInputField,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
  Markdown,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from "@atlas/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { mainContent } from "./documents/mainContent";
import { SIDE_OPTIONS } from "./sideOptions";

export interface TermsAndConditionsDocument {
  id: string;
  label: string;
  content: string;
}

export interface TermsAndConditionsDetails {
  id: string;
  label: string;
  description: string;
  documents: TermsAndConditionsDocument[];
}

export const termsMarkdownClassName =
  "[&_h1]:mb-2 [&_h1]:text-[20px] [&_h1]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-sm [&_h3]:font-medium [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p]:text-justify [&_strong]:font-semibold [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5";

export interface TermsAcceptancePayload {
  signature: string;
}

interface TermsAndConditionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: (payload: TermsAcceptancePayload) => void;
  userName: string;
  userEmail: string;
  companyName: string;
  isAccepting?: boolean;
}

const termsAndConditionsFormSchema = z.object({
  enterYourName: z
    .string()
    .min(1, "This field is required")
    .max(30, "Maximum 30 characters allowed")
    .regex(/^[a-zA-Z0-9 ]+$/, "Only alphabets, numbers and spaces are allowed"),
});

type TermsAndConditionsFormData = z.infer<typeof termsAndConditionsFormSchema>;

const DEFAULT_TERMS_AND_CONDITIONS_FORM_VALUES: TermsAndConditionsFormData = {
  enterYourName: "",
};

function TermsReadOnlyItemField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Item size="sm" className="p-0">
      <ItemContent>
        <ItemTitle>{label}</ItemTitle>
        <ItemDescription>{value}</ItemDescription>
      </ItemContent>
    </Item>
  );
}

function TermsSectionDivider() {
  return <hr className="bg-border h-1 w-full shrink-0 border-0" />;
}

export function TermsAndConditionsDialog({
  open,
  onOpenChange,
  onAccept,
  userName,
  userEmail,
  companyName,
  isAccepting = false,
}: TermsAndConditionsDialogProps) {
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] =
    useState(false);

  const form = useForm<TermsAndConditionsFormData>({
    mode: "onChange",
    resolver: zodResolver(termsAndConditionsFormSchema),
    defaultValues: DEFAULT_TERMS_AND_CONDITIONS_FORM_VALUES,
  });
  const {
    control,
    reset,
    getValues,
    formState: { isValid: hasValidEnteredName },
  } = form;
  const displayDate = open ? new Date().toISOString().split("T")[0] : "";

  useEffect(() => {
    if (!open) {
      setAcceptTermsAndConditions(false);
    }

    reset(DEFAULT_TERMS_AND_CONDITIONS_FORM_VALUES);
  }, [open, reset]);

  useEffect(() => {
    if (!hasValidEnteredName) {
      setAcceptTermsAndConditions(false);
    }
  }, [hasValidEnteredName]);

  const handleAccept = () => {
    const { enterYourName } = getValues();
    onAccept({
      signature: enterYourName.trim(),
    });
  };

  const handleDeny = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="xl"
        className="flex max-h-[70vh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-large text-base font-bold">
            Terms and Conditions
          </DialogTitle>
          <DialogDescription className="text-(--secondary-foreground)">
            Iron Mountain works hard to personalize each supplier relationship,
            so purchase orders vary in content. However here are the general
            terms and conditions for purchase orders in the regions we do
            business in:
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="min-h-0 flex-1 overflow-y-auto p-1 pr-4">
          <div className="flex flex-col gap-6">
            <Markdown className={termsMarkdownClassName}>
              {mainContent}
            </Markdown>

            <TermsSectionDivider />

            {SIDE_OPTIONS.map((option) => (
              <div key={option.id} className="flex flex-col gap-6">
                <div className="flex flex-col">
                  <Typography.H3 className="mb-0 text-base leading-tight font-bold">
                    {option.label}
                  </Typography.H3>
                  {option.description ? (
                    <Typography.P className="mt-0 mb-0 text-justify text-sm leading-snug text-(--secondary-foreground)">
                      {option.description}
                    </Typography.P>
                  ) : null}
                </div>
                {option.documents.map((document, documentIndex) => (
                  <section key={document.id}>
                    <Typography.H4 className="mb-3 text-sm font-semibold">
                      {documentIndex + 1}. {document.label}
                    </Typography.H4>
                    <Markdown className={termsMarkdownClassName}>
                      {document.content}
                    </Markdown>
                  </section>
                ))}
                <TermsSectionDivider />
              </div>
            ))}

            <div className="flex flex-col gap-4">
              <Typography.H3 className="text-base font-bold">
                Please check your details and enter your name
              </Typography.H3>
              <Form {...form}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <TermsReadOnlyItemField label="Name" value={userName} />
                  <TermsReadOnlyItemField label="Company" value={companyName} />
                  <TermsReadOnlyItemField
                    label="Email address"
                    value={userEmail}
                  />
                  <TermsReadOnlyItemField label="Date" value={displayDate} />
                  <FormInputField<TermsAndConditionsFormData>
                    control={control}
                    name="enterYourName"
                    label="Enter Your Name"
                    placeholder="Enter your name"
                    required
                  />
                </div>
              </Form>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="bg-popover items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {!hasValidEnteredName ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-not-allowed">
                    <Checkbox
                      id="accept-terms-and-conditions"
                      checked={acceptTermsAndConditions}
                      onCheckedChange={(checked) =>
                        setAcceptTermsAndConditions(Boolean(checked))
                      }
                      disabled
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Please enter your name</TooltipContent>
              </Tooltip>
            ) : (
              <Checkbox
                id="accept-terms-and-conditions"
                checked={acceptTermsAndConditions}
                onCheckedChange={(checked) =>
                  setAcceptTermsAndConditions(Boolean(checked))
                }
              />
            )}
            <div className="flex min-w-0 items-start gap-1">
              <span className="text-destructive shrink-0 font-bold">*</span>
              <Typography.Muted className="text-foreground text-base leading-normal font-medium">
                I have read and agree to the Iron Mountain InSight® DXP
                Agreement. By selecting “Accept”, you are executing this
                Agreement as of the Effective Date
              </Typography.Muted>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    onClick={handleAccept}
                    disabled={!acceptTermsAndConditions}
                    loading={isAccepting}>
                    Accept
                  </Button>
                </span>
              </TooltipTrigger>
              {!acceptTermsAndConditions ? (
                <TooltipContent>
                  All required fields must be completed before you can accept.
                </TooltipContent>
              ) : null}
            </Tooltip>
            <Button variant="outline" onClick={handleDeny}>
              Deny
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
